import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from "@mui/material";
import axios from "axios";
import apiUrl from "../api";
import PopUpWindow from "../popUpWindow/PopUpWindow";
import AddActionForm from "../addActionForm/AddActionForm";
import ContractForm from "../contractForm/ContractForm";
import ViewPoolActionForm from "../viewPoolActionForm/ViewPoolActionForm";
import { tr } from "date-fns/locale";

const ChargeModelPreview = ({
  open,
  onClose,
  charge,
  title = "Preview Charge Details",
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [modelData, setModelData] = useState(null);
  const user = JSON.parse(localStorage.getItem("user"));

  const fetchModelData = useCallback(async () => {
    if (!charge || !charge.model || !charge.ref_of_model) return;

    setLoading(true);
    setError("");

    try {
      let endpoint = "";

      switch (charge.model) {
        case "Action":
          endpoint = `/action/getAction/${charge.ref_of_model}`;
          break;
        case "PoolAction":
          endpoint = `/pool-action/${charge.ref_of_model}`;
          break;
        case "Contract":
          endpoint = `/contract/${charge.ref_of_model}`;
          break;
        default:
          throw new Error(`Unsupported model type: ${charge.model}`);
      }

      const response = await axios.get(`${apiUrl}${endpoint}`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      });

      // Handle different response structures
      if (charge.model === "Action") {
        setModelData(response.data.action);
      } else if (charge.model === "Contract") {
        setModelData(response.data.contract);
      } else {
        setModelData(response.data); // PoolAction returns data directly
      }
    } catch (err) {
      console.error("Failed to fetch model data:", err);
      setError(`Failed to load ${charge.model} details. Please try again.`);
    } finally {
      setLoading(false);
    }
  }, [charge, user.token]);

  useEffect(() => {
    if (open && charge && charge.model && charge.ref_of_model) {
      fetchModelData();
    }
  }, [open, charge, fetchModelData]);

  const renderModelPreview = () => {
    if (loading) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      );
    }

    if (!modelData) {
      return (
        <Typography color="text.secondary">
          No data available for this charge.
        </Typography>
      );
    }

    switch (charge.model) {
      case "Action":
        return (
          <Box>
            <AddActionForm
              view={true}
              edit={false}
              actionId={modelData._id}
              action_option={{
                label: modelData.action_option.name,
                value: {
                  _id: modelData.action_option._id,
                  name: modelData.action_option.name,
                },
              }}
              property={{
                label: modelData.property.name,
                value: {
                  _id: modelData.property._id,
                  name: modelData.property.name,
                },
              }}
              group={{
                label: modelData.group?.name,
                value: {
                  _id: modelData.group?._id,
                  name: modelData.group?.name,
                },
              }}
              assigned_to={modelData.assigned_to?.map((user) => ({
                label: user?.name || user?.username,
                value: {
                  _id: user?._id,
                  name: user?.name,
                  username: user?.username,
                },
              }))}
              date={new Date(modelData.planned_start_date)}
              notes={modelData.notes}
            />
          </Box>
        );

      case "PoolAction":
        return (
          <Box>
            <ViewPoolActionForm
              poolAction={modelData}
              onClose={() => {}}
              readOnly={true}
            />
          </Box>
        );

      case "Contract":
        return (
          <Box>
            <ContractForm
              contract={modelData}
              noPopUp={true}
              handleClose={() => {

              }}
              readOnly={true}
            />
          </Box>
        );

      default:
        return (
          <Typography color="text.secondary">
            Preview not available for {charge.model} type.
          </Typography>
        );
    }
  };

  const getDialogTitle = () => {
    let titleText = title;
    if (charge) {
      titleText += ` - ${charge.model} | Amount: €${charge.amount} | Remaining: €${charge.remaining_amount}`;
    }
    return titleText;
  };

  if (!open) return null;

  return (
    <PopUpWindow title={getDialogTitle()} onClose={onClose} width="800px">
      {renderModelPreview()}
    </PopUpWindow>
  );
};

export default ChargeModelPreview;
